import React, { useState } from 'react';

import { creditFacilityToBorrowingBaseReceivables } from '@finley-technologies/calc-agent';
import { finleyAssert } from '@finley-technologies/common';
import {
  Util,
  Receivable as ReceivableTypes,
  Receivable,
} from '@finley-technologies/finley-types';
import {
  TapeBuilderUI,
  OperationNodes,
} from '@finley-technologies/tape-builder';

import { getCreditFacilityBySlug, useGraph } from '../GraphContext';
import { ProgressWithDownloadButton } from './ProgressWithDownloadButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

type CFReceivableWithRow = ReceivableTypes.CreditFacility.CreditFacilityReceivable & {
  sanitizedRow: Util.SanitizedCsvRow;
};
type Props = {
  borrowingBaseMechanics: CreditAgreement.Config.ResolvedCalcAgentMechanics;
  receivables: CFReceivableWithRow[];
  tapeBuilderNode: OperationNodes.CalcAgent.UINode;
  reportingDate: Util.FullDateStr;
  onDone: (borrowingBaseDetails: {
    borrowingBaseReceivables: Receivable.CreditFacility.BorrowingBaseReceivable[];
    concentrationLimitCalculations: CreditAgreement.Config.ConcentrationLimit.Calculation.ConcentrationLimitCalculation[];
  }) => void;
};
export const CalcAgentEvaluator = ({
  borrowingBaseMechanics,
  tapeBuilderNode,
  reportingDate,
  receivables,
  onDone,
}: Props) => {
  const graphContext = useGraph();

  finleyAssert(
    TapeBuilderUI.operationNodes.operation_calcAgent.isNode(tapeBuilderNode),
    'node is not a calcAgent node',
  );
  const creditFacilityId = getCreditFacilityBySlug(
    graphContext,
    tapeBuilderNode.configuration.creditFacilitySlug,
  );
  finleyAssert(creditFacilityId, 'creditFacilityId not found');

  const [progressPercent, setProgressPercent] = useState(0);
  const [progressText, setProgressText] = useState('');

  const runCalcAgent = async () => {
    finleyAssert(
      receivables.length > 0,
      'No receivables, Did you forget to run Intermediate Calcs?',
    );

    const {
      borrowingBaseReceivables,
      concentrationLimitCalculations,
    } = await creditFacilityToBorrowingBaseReceivables({
      loanTapeMetadata: { reportingDate },
      creditFacilityReceivables: receivables,
      borrowingBaseMechanics,
      progressCallback: async (
        operation: string,
        strProgress: string,
        percent: number,
      ) => {
        setProgressText(`${operation}: ${strProgress}`);
        setProgressPercent(percent);
        await new Promise((resolve) => setTimeout(resolve, 0));
      },
      receivableWaivers: [], // do not use receivable-level eligibility / concentration waivers for admin calc agent QA
      f1EngineMode: undefined, // TODO: @sachinr4 - 2Fast2Formula
    });
    setProgressPercent(100);
    setProgressText('Done!');
    onDone({
      borrowingBaseReceivables,
      concentrationLimitCalculations,
    });
  };

  const filename = `calc-agent-${tapeBuilderNode.configuration.creditFacilitySlug}-${reportingDate}.json`;
  return (
    <ProgressWithDownloadButton
      onStart={runCalcAgent}
      buttonText="Calc Agent"
      progressPercent={progressPercent}
      progressText={progressText}
      filename={filename}
    />
  );
};
